import {
  MULTI_DESTINATION,
  ONE_WAY,
  RETURN,
} from '@web-platform/shared-universal-constants/src/tripTypes';
import { SEARCH_PARAMS_CHANGED } from '@web-platform/shared-universal-constants/src/userSearch';
import { getPlaceOfStay } from '@web-platform/shared-universal-utils-frontend/src/helpers/getPlaceOfStay';
import { isAcornRequest } from '@web-platform/shared-universal-utils-frontend/src/helpers/isAcornRequest';

import { APP_INIT } from '../../../../actions/app';

import { getInitialState } from './initialState';

import type { Action } from '@web-platform/shared-universal-types/src/redux/actions/actions-shared-types';
import type {
  SearchState,
  PartialSearchParams,
} from '@web-platform/shared-universal-types/src/redux/state/appInitial/user/search';

const getUpdatedTripType = ({ legs, tripType }: PartialSearchParams) => {
  // Fix inconsistent tripType: if MULTI_DESTINATION but legs are not multi-leg, correct it
  // This handles edge cases where users have stale MULTI_DESTINATION state from previous searches
  if (tripType === MULTI_DESTINATION && legs && legs.length < 3) {
    const isOneWay = legs.length === 1 && !legs[0].return_date;
    return isOneWay ? ONE_WAY : RETURN;
  }
  return tripType;
};

export const onSearchParamsChanged = (
  state: SearchState,
  searchParams: PartialSearchParams,
): SearchState => {
  if (isAcornRequest()) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return { ...state, ...searchParams } as SearchState;
  }

  const tripType = getUpdatedTripType(searchParams) || state.tripType;

  if (tripType === MULTI_DESTINATION || state.legs.length !== 1) {
    // We have to cast here because in theory we could be merging in Partial objects
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return { ...state, ...searchParams } as SearchState;
  }

  let onlyLeg = state.legs[0];

  if (searchParams.legs?.length === 1) {
    onlyLeg = { ...onlyLeg, ...searchParams.legs[0] };
  }

  if (searchParams.tripType === ONE_WAY || onlyLeg.return_date === null) {
    delete onlyLeg.return_date;
  }

  // we want to merge options as we set some in INIT for speedcurve
  let { options } = state;
  if (searchParams.options) {
    options = { ...options, ...searchParams.options };
  }

  let { placeOfStay } = state.legs[0];
  const destination = searchParams.legs && searchParams.legs[0] && searchParams.legs[0].destination;

  if (destination) {
    placeOfStay = getPlaceOfStay(destination);
  }

  return {
    ...state,
    ...searchParams,
    legs: [{ ...onlyLeg, placeOfStay }],
    tripType,
    options,
  };
};

// FIXME: SHIBA-809 Bad typecasting - do not copy this pattern
const search = (state: SearchState = getInitialState(), action: Action): SearchState => {
  switch (action.type) {
    case SEARCH_PARAMS_CHANGED: {
      const { searchParams } = action.payload;

      return onSearchParamsChanged(state, searchParams);
    }

    case APP_INIT: {
      if (!action.payload) {
        return state;
      }

      const { payload } = action;
      const { nearestOrigin, origin } = payload;
      // ST-4589 Update SearchState types to reflect the fact we're setting `null` date here.
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      return {
        ...state,
        legs: [{ ...state.legs[0], origin }],
        nearestOrigin,
      } as SearchState;
    }

    default:
      return state;
  }
};

export default search;
export const get = (state: SearchState): SearchState => state;
